const API_ROOT = 'https://fapi.binance.com/fapi/v1';
const REQUEST_TIMEOUT_MS = 15_000;
const MAX_REQUEST_BARS = 5000;
const MAX_CACHED_BARS = 10_000;
const RESOLUTIONS = Object.freeze({
  '15': { interval: '15m', seconds: 15 * 60 },
  '240': { interval: '4h', seconds: 4 * 60 * 60 },
  '1D': { interval: '1d', seconds: 24 * 60 * 60 },
  '1W': { interval: '1w', seconds: 7 * 24 * 60 * 60 },
});
const SUPPORTED_RESOLUTIONS = Object.keys(RESOLUTIONS);

function resolutionSettings(resolution) {
  if (!Object.hasOwn(RESOLUTIONS, resolution)) throw new Error('Unsupported reference chart timeframe.');
  return RESOLUTIONS[resolution];
}

function candleOpen(timeSeconds, resolution) {
  const { seconds } = resolutionSettings(resolution);
  // Unix epoch was Thursday; Binance weekly candles start Monday 00:00 UTC.
  const offset = resolution === '1W' ? 4 * 24 * 60 * 60 : 0;
  return (Math.floor((timeSeconds - offset) / seconds) * seconds + offset) * 1000;
}

function priceScale(tickSize) {
  if (typeof tickSize !== 'string' || !/^\d+(?:\.\d+)?$/.test(tickSize)) {
    throw new Error('Binance returned an invalid price tick.');
  }
  const tick = Number(tickSize);
  const scale = Math.round(1 / tick);
  if (!(tick > 0) || !Number.isSafeInteger(scale) || scale < 1
      || Math.abs(scale * tick - 1) > 1e-10) {
    throw new Error('This futures price tick is not supported by the reference chart.');
  }
  return scale;
}

function parseBars(rows, limit, endTime, resolution, cutoffExclusive) {
  if (!Array.isArray(rows) || rows.length > limit) throw new Error('Binance returned invalid candle data.');
  let previous = -1;
  return rows.map((row) => {
    if (!Array.isArray(row) || row.length < 7 || !Number.isSafeInteger(row[0]) || row[0] < 0
        || row[0] > endTime || row[0] <= previous || candleOpen(row[0] / 1000, resolution) !== row[0]) {
      throw new Error('Binance returned invalid candle times.');
    }
    // Binance endTime filters OPEN times. Independently validate the scheduled
    // close and completion boundary before any setup bar reaches an indicator.
    if (cutoffExclusive != null && (!Number.isSafeInteger(row[6])
        || row[6] !== row[0] + resolutionSettings(resolution).seconds * 1000 - 1
        || row[6] >= cutoffExclusive * 1000)) {
      throw new Error('Binance returned a candle outside the completed-candle cutoff.');
    }
    if (!row.slice(1, 6).every((value) => typeof value === 'string' && /^\d+(?:\.\d+)?$/.test(value))) {
      throw new Error('Binance returned invalid candle prices.');
    }
    const [open, high, low, close, volume] = row.slice(1, 6).map(Number);
    if (![open, high, low, close, volume].every(Number.isFinite) || low <= 0 || volume < 0
        || high < Math.max(open, close, low) || low > Math.min(open, close)) {
      throw new Error('Binance returned invalid candle prices.');
    }
    previous = row[0];
    return { time: row[0], open, high, low, close, volume };
  });
}

/** A private, historical USDT futures feed for a single reference capture. */
export function createReferenceChartDatafeed(symbol, { cutoffExclusive, partialBaseResolution } = {}) {
  if (typeof symbol !== 'string' || !/^[A-Z0-9]{1,30}USDT$/.test(symbol)) {
    throw new Error('Choose a canonical Binance USDT futures symbol.');
  }
  let disposed = false;
  let exchangePromise = null;
  const controllers = new Set();
  const callbackTimers = new Set();
  const loaded = new Map();
  const partialCandles = new Map();
  const snapshotEnd = Date.now();
  if (cutoffExclusive != null && (!Number.isSafeInteger(cutoffExclusive)
      || cutoffExclusive <= 0 || !Number.isSafeInteger(cutoffExclusive * 1000)
      || cutoffExclusive * 1000 > snapshotEnd)) {
    throw new Error('Choose a candle that has already closed.');
  }
  if (partialBaseResolution != null && (cutoffExclusive == null
      || candleOpen(cutoffExclusive, partialBaseResolution) !== cutoffExclusive * 1000)) {
    throw new Error('The developing candle requires a closed base candle cutoff.');
  }

  function deliver(callback, ...args) {
    if (disposed) return;
    const timer = setTimeout(() => {
      callbackTimers.delete(timer);
      if (!disposed) callback(...args);
    }, 0);
    callbackTimers.add(timer);
  }

  async function request(endpoint, params) {
    if (disposed) throw new Error('Reference chart is closed.');
    const controller = new AbortController();
    controllers.add(controller);
    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; controller.abort(); }, REQUEST_TIMEOUT_MS);
    try {
      const query = params ? `?${new URLSearchParams(params)}` : '';
      const response = await fetch(`${API_ROOT}/${endpoint}${query}`, {
        signal: controller.signal,
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Binance futures request failed (HTTP ${response.status}).`);
      let data;
      try { data = await response.json(); } catch (error) {
        if (controller.signal.aborted) throw error;
        throw new Error('Binance futures returned an invalid JSON response.');
      }
      if (data && !Array.isArray(data) && Object.hasOwn(data, 'code') && data.code !== 0) {
        throw new Error('Binance futures rejected the chart data request.');
      }
      if (disposed) throw new Error('Reference chart is closed.');
      return data;
    } catch (error) {
      if (timedOut) throw new Error('Binance futures request timed out. Please try again.');
      throw error;
    } finally {
      clearTimeout(timer);
      controllers.delete(controller);
    }
  }

  function resolveExchangeSymbol() {
    if (!exchangePromise) {
      exchangePromise = request('exchangeInfo').then((data) => {
        if (!data || !Array.isArray(data.symbols)) throw new Error('Binance returned invalid futures exchange information.');
        const entry = data.symbols.find((candidate) => candidate?.symbol === symbol);
        if (!entry || entry.status !== 'TRADING' || entry.quoteAsset !== 'USDT' || entry.marginAsset !== 'USDT') {
          throw new Error('This symbol is not a trading Binance USDT futures pair.');
        }
        const tick = Array.isArray(entry.filters)
          ? entry.filters.find((filter) => filter?.filterType === 'PRICE_FILTER')?.tickSize : undefined;
        return { pricescale: priceScale(tick) };
      }).catch((error) => { exchangePromise = null; throw error; });
    }
    return exchangePromise;
  }

  function remember(resolution, bars) {
    const cache = loaded.get(resolution) || new Map();
    const snapshot = bars.map((bar) => {
      // Keep a candle stable if the widget requests the same partial bar again.
      const value = cache.get(bar.time) || { ...bar };
      cache.delete(bar.time);
      cache.set(bar.time, value);
      return { ...value };
    });
    while (cache.size > MAX_CACHED_BARS) cache.delete(cache.keys().next().value);
    loaded.set(resolution, cache);
    return snapshot;
  }

  function developingCandle(resolution) {
    if (!partialCandles.has(resolution)) {
      const promise = (async () => {
        const settings = resolutionSettings(resolution);
        const base = resolutionSettings(partialBaseResolution);
        const start = candleOpen(cutoffExclusive, resolution);
        const end = cutoffExclusive * 1000;
        const step = base.seconds * 1000;
        const count = (end - start) / step;
        if (base.seconds >= settings.seconds || settings.seconds % base.seconds !== 0
            || candleOpen(start / 1000, partialBaseResolution) !== start
            || !Number.isSafeInteger(count) || count < 1 || count > 1000) {
          throw new Error('Cannot reconstruct this higher timeframe from the selected base timeframe.');
        }
        const rows = await request('klines', {
          symbol, interval: base.interval, startTime: String(start), endTime: String(end - 1), limit: String(count),
        });
        const bars = parseBars(rows, count, end - 1, partialBaseResolution, cutoffExclusive);
        if (bars.length !== count || bars.some((bar, index) => bar.time !== start + index * step)) {
          throw new Error('The developing higher candle is missing base candles. Capture again when the data is available.');
        }
        const volume = bars.reduce((sum, bar) => sum + bar.volume, 0);
        if (!Number.isFinite(volume)) throw new Error('The developing higher candle has invalid volume.');
        return {
          time: start, open: bars[0].open, high: Math.max(...bars.map((bar) => bar.high)),
          low: Math.min(...bars.map((bar) => bar.low)), close: bars[bars.length - 1].close, volume,
        };
      })().catch((error) => { partialCandles.delete(resolution); throw error; });
      partialCandles.set(resolution, promise);
    }
    return partialCandles.get(resolution);
  }

  const datafeed = {
    onReady(callback) {
      deliver(callback, {
        supported_resolutions: [...SUPPORTED_RESOLUTIONS],
        supports_search: false, supports_group_request: false,
        supports_marks: false, supports_timescale_marks: false, supports_time: false,
        exchanges: [{ value: 'BINANCE', name: 'Binance Futures', desc: 'Binance USDT Futures' }],
        symbols_types: [{ name: 'crypto', value: 'crypto' }],
      });
    },

    searchSymbols(_input, _exchange, _type, callback) { deliver(callback, []); },

    async resolveSymbol(symbolName, onResolve, onError) {
      try {
        if (String(symbolName).split(':').pop().replace('/', '') !== symbol) throw new Error('Reference chart symbol cannot be changed.');
        const info = await resolveExchangeSymbol();
        deliver(onResolve, {
          ticker: symbol, name: symbol, description: `${symbol} · Binance USDT Futures`,
          type: 'crypto', exchange: 'BINANCE', listed_exchange: 'BINANCE',
          session: '24x7', timezone: 'Etc/UTC', format: 'price', minmov: 1, pricescale: info.pricescale,
          has_intraday: true, intraday_multipliers: ['15', '240'],
          has_daily: true, daily_multipliers: ['1'],
          has_weekly_and_monthly: true, weekly_multipliers: ['1'], monthly_multipliers: [],
          supported_resolutions: [...SUPPORTED_RESOLUTIONS], visible_plots_set: 'ohlcv',
          volume_precision: 8, data_status: 'endofday',
        });
      } catch (error) { deliver(onError, error.message || 'Could not resolve Binance futures symbol.'); }
    },

    async getBars(_symbolInfo, resolution, period, onHistory, onError) {
      try {
        const settings = resolutionSettings(resolution);
        const { from, to, countBack } = period || {};
        if (!Number.isFinite(from) || !Number.isFinite(to) || from < 0 || to <= from
            || !Number.isSafeInteger(Math.ceil(to * 1000))
            || (countBack !== undefined && (!Number.isSafeInteger(countBack) || countBack < 1))) {
          throw new Error('Invalid reference chart history range.');
        }
        await resolveExchangeSymbol();
        const target = Math.min(MAX_REQUEST_BARS, countBack || Math.max(1, Math.ceil((to - from) / settings.seconds)));
        // endTime is inclusive on Binance, while TradingView's `to` is exclusive.
        // Omitting startTime asks for the latest page before this boundary, avoiding
        // a truncated first page from the oldest end of a large requested range.
        let endTime = Math.min(Math.ceil(to * 1000) - 1, snapshotEnd);
        // Never load the exchange's final HTF candle across the decision cutoff.
        // An optional developing bar is reconstructed solely from closed base bars.
        if (cutoffExclusive != null) endTime = Math.min(endTime, candleOpen(cutoffExclusive, resolution) - 1);
        let bars = [];
        if (partialBaseResolution != null) {
          const partialOpen = candleOpen(cutoffExclusive, resolution);
          if (partialOpen < cutoffExclusive * 1000 && partialOpen < to * 1000) {
            bars = [await developingCandle(resolution)];
          }
        }
        let exhausted = false;
        while (bars.length < target && !disposed) {
          if (endTime < 0) { exhausted = true; break; }
          const limit = Math.min(1000, target - bars.length);
          const rows = await request('klines', { symbol, interval: settings.interval, endTime: String(endTime), limit: String(limit) });
          const page = parseBars(rows, limit, endTime, resolution, cutoffExclusive);
          if (!page.length) { exhausted = true; break; }
          bars = [...page, ...bars];
          endTime = page[0].time - 1;
          if (page.length < limit) { exhausted = true; break; }
        }
        if (disposed) return;
        // Earlier candles are deliberately retained to satisfy countBack even
        // when TradingView's calculated `from` range contains fewer candles.
        deliver(onHistory, remember(resolution, bars), { noData: bars.length === 0 && exhausted });
      } catch (error) { deliver(onError, error.message || 'Could not load Binance futures candles.'); }
    },

    subscribeBars() {},
    unsubscribeBars() {},
  };

  return {
    datafeed,
    hasCandle(timeSeconds, resolution) {
      if (disposed || !Number.isFinite(timeSeconds) || timeSeconds < 0 || timeSeconds * 1000 > snapshotEnd
          || !Object.hasOwn(RESOLUTIONS, resolution)) return false;
      return loaded.get(resolution)?.has(candleOpen(timeSeconds, resolution)) || false;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const controller of controllers) controller.abort();
      controllers.clear();
      for (const timer of callbackTimers) clearTimeout(timer);
      callbackTimers.clear();
      loaded.clear();
      partialCandles.clear();
      exchangePromise = null;
    },
  };
}
